/**
 * 指标体系相关的工具函数
 */

/**
 * 标准化指标权重，确保总权重为100%，但保持原有比例
 * 不强制按照15/25/35/25调整，尊重用户的指标分布
 *
 * @param {Array<Object>} indicators - 待调整的指标列表
 * @returns {Array<Object>} 调整后的指标列表
 */
export function adjustIndicatorWeights(indicators: any[]): any[] {
  console.log(`[权重调整] 执行权重标准化，保持原有比例`);

  if (!indicators || indicators.length === 0) {
    return indicators;
  }

  // 计算当前总权重
  const currentTotalWeight = indicators.reduce((sum, ind) => sum + (Number(ind.weight) || 0), 0);
  
  if (currentTotalWeight === 0) {
    // 如果没有权重，平均分配
    const avgWeight = Math.round((100 / indicators.length) * 2) / 2; // 0.5精度
    return indicators.map(ind => ({
      ...ind,
      weight: avgWeight
    }));
  }

  if (Math.abs(currentTotalWeight - 100) < 0.01) {
    // 如果总权重已经是100%，直接返回
    console.log('[权重调整] 总权重已为100%，无需调整');
    return indicators;
  }

  // 按比例调整所有权重，使总和为100%
  const scaleFactor = 100 / currentTotalWeight;
  const adjustedIndicators = indicators.map(ind => {
    const originalWeight = Number(ind.weight) || 0;
    const scaledWeight = originalWeight * scaleFactor;
    // 保持0.5精度
    const finalWeight = Math.round(scaledWeight * 2) / 2;
    return {
      ...ind,
      weight: Math.max(0.5, finalWeight) // 最小权重0.5
    };
  });

  // 微调确保精确等于100%
  const finalTotal = adjustedIndicators.reduce((sum, ind) => sum + ind.weight, 0);
  const difference = 100 - finalTotal;
  
  if (Math.abs(difference) > 0.01) {
    // 将差值分配给权重最大的指标
    const maxWeightIndex = adjustedIndicators.reduce((maxIndex, ind, index) => 
      ind.weight > adjustedIndicators[maxIndex].weight ? index : maxIndex, 0);
    
    adjustedIndicators[maxWeightIndex].weight = 
      Math.round((adjustedIndicators[maxWeightIndex].weight + difference) * 2) / 2;
  }

  const finalTotalWeight = adjustedIndicators.reduce((sum, ind) => sum + ind.weight, 0);
  console.log(`[权重调整] 权重标准化完成。最终总权重: ${finalTotalWeight}%`);
  
  return adjustedIndicators;
}

/**
 * 精确分配权重，确保结果为0.5的倍数，并尽可能均匀
 *
 * @param {number} totalWeight - 该组的总权重
 * @param {number} count - 该组的指标数量
 * @returns {Array<number>} 分配好的权重数组
 */
export function distributeWeightPrecisely(totalWeight: number, count: number): number[] {
  if (count === 0) return [];
  
  // 转换为0.5的倍数单位
  const totalUnits = Math.round(totalWeight * 2);
  const baseUnitsPerIndicator = Math.floor(totalUnits / count);
  const remainderUnits = totalUnits % count;
  
  // 基础分配
  const weightsInUnits = new Array(count).fill(baseUnitsPerIndicator);
  
  // 分配余数
  for (let i = 0; i < remainderUnits; i++) {
    weightsInUnits[i]++;
  }

  // 转换为实际权重
  return weightsInUnits.map(unit => unit / 2);
}

/**
 * 通过微调，确保一组指标的总权重正好等于目标值
 *
 * @param {Array<Object>} indicators - 待调整的指标列表
 * @param {number} targetTotal - 目标总权重
 * @returns {Array<Object>} 调整后的指标列表
 */
export function ensureExactWeight(indicators: any[], targetTotal: number): any[] {
  if (!indicators || indicators.length === 0) return [];
  
  let currentTotal = indicators.reduce((sum, ind) => sum + ind.weight, 0);
  let difference = targetTotal - currentTotal;
  
  // 以0.5为单位调整
  let diffUnits = Math.round(difference * 2);

  if (diffUnits !== 0) {
    const adjustmentPerIndicator = diffUnits > 0 ? 1 : -1;
    const unitAdjustment = adjustmentPerIndicator * 0.5;

    for (let i = 0; i < Math.abs(diffUnits); i++) {
      // 优先调整权重较大/较小的指标，避免权重为0或负数
      indicators.sort((a, b) => 
        unitAdjustment > 0 
        ? a.weight - b.weight // 加权时，给权重小的加
        : b.weight - a.weight // 减权时，给权重大的减
      );
      
      const indicatorToAdjust = indicators[i % indicators.length];
      const newWeight = indicatorToAdjust.weight + unitAdjustment;

      // 避免权重低于0.5
      if (newWeight >= 0.5) {
        indicatorToAdjust.weight = newWeight;
      } else {
        // 如果调整会导致权重过低，则尝试调整下一个
        const nextIndicator = indicators[(i + 1) % indicators.length];
        if (nextIndicator) {
            const nextNewWeight = nextIndicator.weight + unitAdjustment;
             if (nextNewWeight >= 0.5) {
                nextIndicator.weight = nextNewWeight;
             }
        }
      }
    }
  }

  // 最终校验
  const finalTotal = indicators.reduce((sum, ind) => sum + ind.weight, 0);
  if (Math.abs(finalTotal - targetTotal) > 0.01) {
    console.warn(`[权重微调] 最终总和 ${finalTotal} 与目标 ${targetTotal} 仍有偏差`);
  }
  
  return indicators;
}

/**
 * 验证指标体系结构的完整性和合理性
 * 不强制检查15/25/35/25比例，允许用户灵活配置
 *
 * @param {Array<Object>} indicators - 指标列表
 * @returns {Object} 包含 isValid (boolean) 和 issues (Array<string>) 的验证结果
 */
export function validateIndicatorStructure(indicators: any[]): { isValid: boolean; issues: string[] } {
  const issues: string[] = [];
  
  if (!indicators || indicators.length === 0) {
    issues.push('没有找到任何指标');
    return { isValid: false, issues };
  }

  // 检查一级指标
  const level1Names = [...new Set(indicators.map(ind => ind.level1_name).filter(Boolean))];
  if (level1Names.length === 0) {
    issues.push('缺少一级指标');
  }

  // 检查权重总和
  const totalWeight = indicators.reduce((sum, ind) => sum + (Number(ind.weight) || 0), 0);
  if (Math.abs(totalWeight - 100) > 0.01) {
    issues.push(`权重总和为 ${totalWeight}%，应为100%`);
  }

  // 检查权重分布（暂时注释掉，避免未使用变量警告）
  // const level1Weights = level1Names.map(level1 => {
  //   const level1Indicators = indicators.filter(ind => ind.level1_name === level1);
  //   return {
  //     level1,
  //     weight: level1Indicators.reduce((sum, ind) => sum + (Number(ind.weight) || 0), 0),
  //     count: level1Indicators.length
  //   };
  // });

  // 检查是否有权重为0的指标
  const zeroWeightIndicators = indicators.filter(ind => !ind.weight || ind.weight === 0);
  if (zeroWeightIndicators.length > 0) {
    issues.push(`有 ${zeroWeightIndicators.length} 个指标的权重为0`);
  }

  return {
    isValid: issues.length === 0,
    issues
  };
}
